import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import qs from 'qs';
import { Status } from '../../models/common/Status';
import { apiClientWithApi } from '../../utils/http';
import {
  resolveXlsxFileName,
  triggerBlobDownload,
} from '../../utils/http/responseHelpers';

export interface ReportState {
  status: Status;
}

export interface ReportRequest {
  From: string;
  To: string;
  ClinicIds?: string[];
  SpecializationIds?: string[];
  DoctorIds?: string[];
  States?: string[];
  AttemptStates?: string[];
  Result?: string[];
  Results?: string[];
  Types?: string[];
  NotificationTypes?: string[];
  readonly FormTemplateIds?: string[];
  readonly FormBatchIds?: string[];
  readonly DispensarizationBatchIds?: string[];
  AttemptFrom?: string;
  AttemptTo?: string;
  TextSearch?: string;
}

export type ReportKind = 'journal' | 'summary';

interface SelectedReportsRequest {
  request: ReportRequest;
  reportKinds: readonly ReportKind[];
}

const initialState: ReportState = {
  status: {
    responseLoading: false,
    responseError: null,
    requestSuccess: null,
    requestError: null,
  },
};

const formatDateForFileName = (date: string): string => {
  const [year, month, day] = date.split('-');
  return `${day}.${month}.${year}`;
};

const reportPeriod = (request: ReportRequest): string =>
  `${formatDateForFileName(request.From)}-${formatDateForFileName(request.To)}`;

const REPORT_CONFIG = {
  journal: {
    path: 'task/outbound/report',
    fallbackFileName: (request: ReportRequest) =>
      `journal_${reportPeriod(request)}.xlsx`,
  },
  summary: {
    path: 'task/slot/report/summary',
    fallbackFileName: (request: ReportRequest) =>
      `Сводныи отчет по заданиям_${reportPeriod(request)}.xlsx`,
  },
} as const satisfies Record<
  ReportKind,
  {
    readonly path: string;
    readonly fallbackFileName: (request: ReportRequest) => string;
  }
>;

const buildReportParams = (request: ReportRequest) => ({
  From: request.From,
  To: request.To,
  ClinicIds: request.ClinicIds,
  States: request.States,
  AttemptStates: request.AttemptStates,
  Result: request.Result ?? request.Results,
  Types: request.Types ?? request.NotificationTypes,
  FormTemplateIds: request.FormTemplateIds,
  FormBatchIds: request.FormBatchIds,
  DispensarizationBatchIds: request.DispensarizationBatchIds,
  TextSearch: request.TextSearch,
  AttemptFrom: request.AttemptFrom,
  AttemptTo: request.AttemptTo,
});

const downloadReport = async (
  reportKind: ReportKind,
  request: ReportRequest,
): Promise<string> => {
  const config = REPORT_CONFIG[reportKind];
  const response = await apiClientWithApi.get(config.path, {
    params: buildReportParams(request),
    paramsSerializer: (params) =>
      qs.stringify(params, { arrayFormat: 'repeat' }),
    responseType: 'blob',
  });
  const fileName = resolveXlsxFileName(
    response.headers['content-disposition'],
    config.fallbackFileName(request),
  );

  triggerBlobDownload(new Blob([response.data]), fileName);
  return fileName;
};

export const getReport = createAsyncThunk(
  'Report/getReport',
  async (request: ReportRequest, { rejectWithValue }) => {
    try {
      return await downloadReport('journal', request);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue(
        error instanceof Error ? error.message : 'Не удалось получить отчёт!',
      );
    }
  },
);

export const getSelectedReports = createAsyncThunk(
  'Report/getSelectedReports',
  async (
    { request, reportKinds }: SelectedReportsRequest,
    { rejectWithValue },
  ) => {
    try {
      const fileNames: string[] = [];

      for (const reportKind of reportKinds) {
        fileNames.push(await downloadReport(reportKind, request));
      }

      return fileNames;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue(
        error instanceof Error ? error.message : 'Не удалось получить отчёт!',
      );
    }
  },
);

export const reportSlice = createSlice({
  name: 'report',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(getReport.pending, (state) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
        return state;
      })
      .addCase(getReport.fulfilled, (state) => {
        state.status.responseLoading = false;
        state.status.responseError = null;
        return state;
      })
      .addCase(getReport.rejected, (state) => {
        state.status.responseLoading = false;
        state.status.responseError = 'Не удалось загрузить звонки';
        return state;
      })
      .addCase(getSelectedReports.pending, (state) => {
        state.status.responseLoading = true;
        state.status.responseError = null;
        return state;
      })
      .addCase(getSelectedReports.fulfilled, (state) => {
        state.status.responseLoading = false;
        state.status.responseError = null;
        return state;
      })
      .addCase(getSelectedReports.rejected, (state) => {
        state.status.responseLoading = false;
        state.status.responseError = 'Не удалось загрузить звонки';
        return state;
      });
  },
});

export default reportSlice.reducer;

export const selectReportLoading = (state: { report: ReportState }) =>
  state.report.status.responseLoading;
